import { useQueryClient } from "@tanstack/react-query"
import { useParams } from "react-router-dom"

import { invalidateDocumentProject, invalidateDocumentsList } from "@l2r-front/l2r-app-doc"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { l2rApiAxiosInstance as axiosInstance } from "@l2r-front/l2r-query"

import { roadworksQueryKeys } from "../../queries/queryKeys"
import { useRoadworksProject } from "../../queries/useRoadworksProjectQuery"

import { useRoadworksMutation } from "./useRoadworksMutation"

export function useRoadworkUpdate(
    roadwork,
    queryParameters,
    config = {},
) {
    const url = `roadworks/${roadwork?.uuid}/`
    const { data: roadworkProject } = useRoadworksProject()

    const { enabled = true } = config
    const queryClient = useQueryClient()
    const { road } = useParams()

    return useRoadworksMutation(
        url,
        axiosInstance.patch,
        {
            ...queryParameters,
        },
        {
            ...config,
            enabled: roadworkProject && roadwork?.uuid && enabled,
            onSuccess: (data) => {
                queryClient.setQueryData(roadworksQueryKeys["roadwork"](roadwork.uuid), () => data)
                const roadworkGeoJSON = {
                    ...data,
                    properties: { ...data },
                }
                queryClient.setQueryData(roadworksQueryKeys.roadwork(roadwork.uuid, { as_geojson: true }), () => {
                    return roadworkGeoJSON
                })
                queryClient.invalidateQueries({ queryKey: roadworksQueryKeys["project"]() })
                queryClient.invalidateQueries({ queryKey: roadworksQueryKeys.list({ linear_location_road: road }) })
                queryClient.invalidateQueries({ queryKey: roadworksQueryKeys.list() })
                queryClient.invalidateQueries({ queryKey: roadworksQueryKeys.stats() })
                queryClient.invalidateQueries({ queryKey: roadworksQueryKeys.stat_length_unique() })
                queryClient.invalidateQueries({ queryKey: roadworksQueryKeys.years_stats() })
                queryClient.invalidateQueries({ queryKey: roadworksQueryKeys.quartersStats() })
                queryClient.invalidateQueries({ queryKey: roadworksQueryKeys.techniques() })
                queryClient.invalidateQueries({ queryKey: roadworksQueryKeys.layer() })
                config.onSuccess?.(data)
            },
        },
    )
}

export function useRoadworkUpdateDocument(
    roadwork,
    queryParameters,
    config = {},
) {
    const url = `roadworks/${roadwork?.uuid}/`
    const { data: roadworkProject } = useRoadworksProject()
    const queryClient = useQueryClient()
    const { selectedNetwork } = useNetworksStateContext()

    const { enabled = true } = config


    const axiosFn = (url, values) => {
        const formData = new FormData()
        Object.entries(values).forEach(([key, value]) => {
            if (key === "document") {
                Object.entries(values[key]).forEach(([docKey, docValue]) => {
                    if (Array.isArray(docValue)) {
                        formData.append(`${key}.${docKey}`, JSON.stringify(docValue))
                    } else {
                        formData.append(`${key}.${docKey}`, docValue)
                    }
                })
            } else {
                formData.append(key, value)
            }
        })

        const headers = values.document ? { "Content-Type": "multipart/form-data" } : {}

        return axiosInstance.patch(url, formData, {
            headers: headers,
        })
    }

    return useRoadworksMutation(
        url,
        axiosFn,
        {
            ...queryParameters,
        },
        {
            ...config,
            enabled: roadworkProject && roadwork?.uuid && enabled,
            onSuccess: (data) => {
                if (queryClient.getQueryData(roadworksQueryKeys["roadwork"](roadwork.uuid))) {
                    queryClient.setQueryData(roadworksQueryKeys["roadwork"](roadwork.uuid), data)
                }
                invalidateDocumentProject(queryClient, selectedNetwork)
                invalidateDocumentsList(queryClient, selectedNetwork)
                config.onSuccess?.(data)
            },
        },
    )
}